import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { InventoryItemType } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { apiMessage, formatQuantity, useQueryParams } from '../shared';
import type { InventoryItemRow } from './InventoryItemDialog';
import type { WarehouseRow } from './inventoryDefs';
import {
  formatSignedQuantity,
  isSignedDecimal,
  multiplyDecimal,
  signOfDecimal,
  subtractDecimal,
  sumDecimal,
} from './signedDecimal';

const ITEM_TYPES: InventoryItemType[] = Object.values(InventoryItemType);

interface Level {
  inventoryItemId: string;
  warehouseId: string;
  unit: { id: string; code: string } | null;
  onHand: string;
  value: string;
}

/** What a row is, once the shelf has been compared with the ledger. */
type RowState = 'pending' | 'invalid' | 'matched' | 'short' | 'over';

interface CountRow {
  item: InventoryItemRow;
  /** `null` when the ledger could not be read — which is not the same as zero. */
  expected: string | null;
  typed: string;
  variance: string | null;
  varianceValue: string | null;
  state: RowState;
}

/**
 * The row chips are **not** domain statuses.
 *
 * `Matched`, `Over`, `Discrepancy` and `Pending` are readings of `sign(counted −
 * expected)`, not members of any enum in `@/lib/shared-types`, so
 * `toneForStatus` does not cover them and must not be reached for. `PENDING`
 * happens to exist in that table as amber; an uncounted row here is not that
 * `PENDING` and colouring it the same would say a count is running late when it
 * has simply not been typed yet.
 */
const ROW_TONE: Record<RowState, string> = {
  pending: 'bg-slate-100 text-slate-700',
  invalid: 'bg-red-100 text-red-700',
  matched: 'bg-green-100 text-green-700',
  short: 'bg-red-100 text-red-700',
  over: 'bg-sky-100 text-sky-700',
};

/**
 * A physical stock count, as one sheet rather than a repeating line editor.
 *
 * ## What this screen deliberately does not have
 *
 * A stock count is **not a persisted entity**. `POST /stock-movements/count`
 * takes `{ warehouseId, occurredAt?, reason?, lines[] }` and writes
 * `STOCK_COUNT` movements straight through in one transaction; there is no
 * `StockCount`, `CountSheet` or `CountSession` model anywhere in the schema.
 * So there is no count number, no `In progress`, no started-at, no
 * `Save progress` and no blind-count mode — six affordances with nothing behind
 * them. The progress figure below counts rows filled in on *this* form, which is
 * a fact about the form and is labelled as one.
 *
 * `Submit count` is also not wired to `/adjust`, which is the endpoint that can
 * park for approval (202). `/count` has no such branch: it writes through.
 *
 * ## Why the expected column gates the submit
 *
 * The server posts `counted − ledger`. If the ledger read failed and this drew
 * the expected column as zero, one submit would book the entire counted
 * quantity of every line as a correction. So an unreadable balance blanks the
 * column and blocks the button, rather than being rendered as an empty venue.
 */
export function StockCountSheet({
  warehouses,
  initialWarehouseId,
  onClose,
  onPosted,
}: {
  warehouses: WarehouseRow[];
  initialWarehouseId?: string;
  onClose: () => void;
  onPosted: () => void;
}) {
  const { t } = useTranslation();
  const { format: money } = useCurrency();

  const [warehouseId, setWarehouseId] = useState(
    () => initialWarehouseId || warehouses.find((w) => w.isDefault && w.isActive)?.id || '',
  );
  const [itemType, setItemType] = useState('');
  const [search, setSearch] = useState('');
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const items = useApi<unknown>(
    '/inventory/items',
    useQueryParams({ search, type: itemType, limit: 200 }),
  );
  const itemRows = useMemo(() => rowsOf<InventoryItemRow>(items.data).rows, [items.data]);

  const levels = useApi<Level[]>(
    warehouseId ? '/stock-movements/levels' : null,
    useQueryParams({ warehouseId }),
  );
  const expectedUnknown = Boolean(levels.error);

  /** Balance per item in the chosen store. An item with no movements is a real
   *  zero — `balance()` answers zero for it too — so the map's default is `'0'`
   *  only while the read itself succeeded. */
  const expectedByItem = useMemo(() => {
    const map = new Map<string, string>();
    for (const level of rowsOf<Level>(levels.data).rows) {
      map.set(level.inventoryItemId, level.onHand);
    }
    return map;
  }, [levels.data]);

  const rows = useMemo<CountRow[]>(
    () =>
      itemRows.map((item) => {
        const expected = expectedUnknown ? null : (expectedByItem.get(item.id) ?? '0');
        const typed = counted[item.id] ?? '';
        const trimmed = typed.trim();

        if (!trimmed) {
          return { item, expected, typed, variance: null, varianceValue: null, state: 'pending' };
        }
        if (!isSignedDecimal(trimmed)) {
          return { item, expected, typed, variance: null, varianceValue: null, state: 'invalid' };
        }
        if (expected === null) {
          return { item, expected, typed, variance: null, varianceValue: null, state: 'pending' };
        }

        const variance = subtractDecimal(trimmed, expected);
        const sign = signOfDecimal(variance);
        return {
          item,
          expected,
          typed,
          variance,
          // The same arithmetic the server does on the way back:
          // `variance × item.costPrice`, at the moving average of the moment.
          varianceValue: multiplyDecimal(variance, item.costPrice ?? '0'),
          state: sign === 0 ? 'matched' : sign < 0 ? 'short' : 'over',
        };
      }),
    [counted, expectedByItem, expectedUnknown, itemRows],
  );

  const tally = useMemo(() => {
    const of = (state: RowState) => rows.filter((row) => row.state === state).length;
    const countedRows = rows.filter((row) => row.typed.trim() !== '').length;
    return {
      total: rows.length,
      counted: countedRows,
      matched: of('matched'),
      short: of('short'),
      over: of('over'),
      pending: rows.length - countedRows,
      percent: rows.length ? Math.round((countedRows / rows.length) * 100) : 0,
      varianceValue: sumDecimal(
        rows.map((row) => row.varianceValue).filter((v): v is string => v !== null),
      ),
    };
  }, [rows]);

  /** Only what was actually counted. A row left blank is not part of this count. */
  const payloadLines = useMemo(
    () =>
      rows
        .filter((row) => row.typed.trim() !== '' && row.state !== 'invalid')
        .map((row) => ({
          inventoryItemId: row.item.id,
          countedQuantity: row.typed.trim(),
        })),
    [rows],
  );

  const blocked = useMemo(() => {
    if (!warehouseId) return t('operations.inventory.warehouseRequired');
    if (expectedUnknown) {
      return t('operations.inventory.expectedUnknown');
    }
    if (rows.some((row) => row.state === 'invalid')) {
      return t('operations.inventory.quantityInvalid');
    }
    if (!payloadLines.length) return t('operations.inventory.countLinesRequired');
    return null;
  }, [expectedUnknown, payloadLines.length, rows, t, warehouseId]);

  const submit = async () => {
    if (blocked) return;
    setBusy(true);
    try {
      await api.post('/stock-movements/count', {
        warehouseId,
        ...(notes.trim() ? { reason: notes.trim() } : {}),
        lines: payloadLines,
      });
      toast.success(t('operations.inventory.done.count'));
      onPosted();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">{t('operations.inventory.actions.count')}</h2>
        <p className="text-sm text-ink-muted">{t('operations.inventory.countHint')}</p>
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* The design labels this "Location: Main branch". A count is of a
              *warehouse*: `StockCountDto.warehouseId` is required, `Warehouse.
              branchId` is nullable and a branch may hold several, so a branch
              does not identify one. Named for what it is. */}
          <Field label={t('operations.inventory.warehouse')} required>
            <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">{t('operations.filters.choose')}</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </Field>

          {/* The design's "Category" has no column to filter on — `InventoryItem`
              carries `type`, and `Category` belongs to the catalog. */}
          <Field label={t('operations.inventory.itemType')}>
            <Select value={itemType} onChange={(e) => setItemType(e.target.value)}>
              <option value="">{t('operations.filters.any')}</option>
              {ITEM_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`operations.inventory.itemTypes.${type}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('operations.filters.search')}>
            <div className="relative">
              <Icon
                as={Search}
                className="pointer-events-none absolute inset-y-0 inset-s-3 my-auto text-ink-muted"
              />
              <Input
                className="ps-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Field>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* `min-w-0` on both tracks: a grid item defaults to `min-width:auto`,
            which is its min-content width, so the six-column table below would
            widen the track instead of scrolling inside `TableWrap`. */}
        <Card className="min-w-0">
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium">
                {t('operations.inventory.countProgress')}
              </p>
              <p className="text-sm text-ink-muted">
                {t(
                  'operations.inventory.countProgressOf',
                  '{{counted}} of {{total}} items counted ({{percent}}%)',
                  { counted: tally.counted, total: tally.total, percent: tally.percent },
                )}
              </p>
            </div>
            <div
              className="h-1.5 overflow-hidden rounded-full bg-subtle"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={tally.percent}
            >
              <div
                className="h-full bg-accent-solid transition-[width]"
                style={{ width: `${tally.percent}%` }}
              />
            </div>

            {expectedUnknown && (
              <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200">
                {t('operations.inventory.expectedUnknown')}
              </p>
            )}

            {items.error ? (
              <ErrorState message={items.error} onRetry={items.reload} />
            ) : items.loading && !itemRows.length ? (
              <LoadingState label={t('common.loading')} />
            ) : (
              <TableWrap>
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('operations.inventory.item')}</TH>
                      <TH>{t('operations.inventory.defs.items.sku')}</TH>
                      <TH className="text-end">{t('operations.inventory.expected')}</TH>
                      <TH>{t('operations.inventory.counted')}</TH>
                      <TH className="text-end">{t('operations.inventory.variance')}</TH>
                      <TH>{t('common.status')}</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {!rows.length ? (
                      <TableEmpty colSpan={6}>{t('operations.inventory.noItems')}</TableEmpty>
                    ) : (
                      rows.map((row) => (
                        <TR key={row.item.id}>
                          <TD className="font-medium">{row.item.name}</TD>
                          <TD className="text-ink-muted">{row.item.sku ?? '—'}</TD>
                          <TD className="text-end tabular-nums" data-testid={`expected-${row.item.id}`}>
                            {row.expected === null ? '—' : formatQuantity(row.expected)}
                          </TD>
                          <TD>
                            <Input
                              className="h-9 w-24"
                              inputMode="decimal"
                              aria-label={row.item.name}
                              aria-invalid={row.state === 'invalid' || undefined}
                              value={row.typed}
                              onChange={(e) =>
                                setCounted((prev) => ({ ...prev, [row.item.id]: e.target.value }))
                              }
                            />
                          </TD>
                          <TD
                            className={`text-end tabular-nums ${
                              row.state === 'short'
                                ? 'text-red-600 dark:text-red-400'
                                : row.state === 'over'
                                  ? 'text-sky-700 dark:text-sky-400'
                                  : ''
                            }`}
                            data-testid={`variance-${row.item.id}`}
                          >
                            {row.variance === null
                              ? '—'
                              : `${signOfDecimal(row.variance) > 0 ? '+' : ''}${formatSignedQuantity(row.variance)}`}
                          </TD>
                          <TD>
                            <span
                              data-testid={`status-${row.item.id}`}
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ROW_TONE[row.state]}`}
                            >
                              {t(`operations.inventory.countStatus.${row.state}`)}
                            </span>
                          </TD>
                        </TR>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-4">
          <Card>
            <CardContent className="space-y-2 p-4 text-sm">
              <p className="text-base font-semibold">
                {t('operations.inventory.countSummary')}
              </p>
              {(
                [
                  ['totalItems', tally.total],
                  ['counted', tally.counted],
                  ['matched', tally.matched],
                  ['short', tally.short],
                  ['over', tally.over],
                  ['pending', tally.pending],
                ] as const
              ).map(([key, value]) => (
                <div key={key} className="flex items-baseline justify-between gap-3">
                  {/* `min-w-0` so a long label wraps instead of setting the
                      card's width — an untranslated key is one long word. */}
                  <span className="min-w-0 break-words text-ink-muted">
                    {t(`operations.inventory.countSummaryRow.${key}`)}
                  </span>
                  <span className="shrink-0 font-medium tabular-nums">{value}</span>
                </div>
              ))}

              <div className="border-t pt-3">
                <p className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                  {t('operations.inventory.varianceValue')}
                </p>
                <p
                  data-testid="variance-value"
                  className={`text-2xl font-semibold tabular-nums ${
                    signOfDecimal(tally.varianceValue) < 0 ? 'text-red-600 dark:text-red-400' : ''
                  }`}
                  dir="ltr"
                >
                  {money(tally.varianceValue)}
                </p>
                {/* Neither the balance nor the moving average is frozen while
                    this sheet is open, so the figure is what the correction
                    would be worth if it were posted now. */}
                <p className="text-xs text-ink-muted">
                  {t('operations.inventory.varianceValueHint')}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              {/* One reason for the whole count — `StockCountDto.reason`, not a
                  per-line field. Left empty the server writes its own
                  "counted x, ledger y" string. */}
              <Field
                label={t('operations.inventory.countNotes')}
                hint={t('operations.inventory.countNotesHint')}
              >
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* No `Save progress`: there is no count entity to save into, and a button
          that appears to park work and does not is worse than its absence. */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          {/* Stated once. When the ledger is the problem, the banner above the
              table has already said so at the place it applies. */}
          {blocked && !expectedUnknown && <p className="text-xs text-ink-muted">{blocked}</p>}
          <Button
            onClick={() => void submit()}
            disabled={busy || !!blocked}
            title={blocked ?? undefined}
          >
            {t('operations.inventory.submitCount')}
          </Button>
        </div>
      </div>
    </div>
  );
}
